import { useCallback, useState, type ReactNode } from 'react';
import { AreaReviewDialog, type IAreaReviewDialogProps, type IAreaUnderReview } from './AreaReviewDialog';

/**
 * The review of an area submitted for it, wherever a manager's tap opens one
 * (area review stage spec, rulings 5 and 7): `open(taskId, zoneId)` shows the
 * review dialog, `dialog` is what to render (nothing while no review is open).
 *
 * Only the ids are held; `taskOf` resolves the task — the reject's issue carries
 * its profession — when the dialog renders, so `open` stays stable and a caller
 * that memoises on it doesn't re-render when its task list changes. A task gone
 * from under its review takes the dialog with it.
 */
export function useAreaReview(
  siteId: string,
  taskOf: (taskId: string) => IAreaUnderReview['task'] | undefined,
  onDecide: IAreaReviewDialogProps['onDecide'],
): { open: (taskId: string, zoneId: string) => void; dialog: ReactNode } {
  const [reviewing, setReviewing] = useState<{ taskId: string; zoneId: string } | null>(null);
  const open = useCallback((taskId: string, zoneId: string) => setReviewing({ taskId, zoneId }), []);

  const task = reviewing ? taskOf(reviewing.taskId) : undefined;
  const dialog = reviewing && task && (
    <AreaReviewDialog
      siteId={siteId}
      reviewing={{ task, zoneId: reviewing.zoneId }}
      onClose={() => setReviewing(null)}
      onDecide={onDecide}
    />
  );
  return { open, dialog };
}
